import {BlockSettings, LegacyWidget} from '@wordpress/blocks';
import {CONFIG} from '../../../globals/config';
import Edit from './Edit';
import type {AccordionOptions} from '../Accordion';
import type {StyleOptions} from '../WidgetStyles';
import type {MenuOptions} from './Menu';
import type {DisplayOptions} from './Display';
import {__} from '@wordpress/i18n';

export type PreviewOptions = {
	isServerSideRenderRequest: boolean;
	clientId: string;
	sidebarId: string;
}

/**
 * Attributes specific to the widget as well as shared
 * widget attributes.
 *
 * @see \Advanced_Sidebar_Menu\Blocks\Block_Abstract::get_all_attributes
 * @see \Advanced_Sidebar_Menu\Blocks\Pages::get_attributes
 */
export type Attr =
	{
		title?: string,
		include_parent: true,
	}
	& DisplayOptions
	& MenuOptions
	& AccordionOptions
	& StyleOptions
	& PreviewOptions;

export type setAttributes = ( newValue: {
	[attribute in keyof Attr]?: Attr[attribute]
} ) => void;

/**
 * Attributes used for the example preview.
 * Combines some PRO and basic attributes.
 * The PRO attributes will only be sent if PRO is active.
 */
const EXAMPLE: Partial<Attr> = {
	apply_current_page_parent_styles_to_parent: true,
	apply_current_page_styles_to_parent: true,
	block_style: true,
	border: true,
	border_color: '#333',
	bullet_style: 'none',
	child_page_bg_color: '#666',
	child_page_color: '#fff',
	current_page_bg_color: '#282828',
	current_page_color: '#0cc4c6',
	current_page_font_weight: 'normal',
	current_page_parent_bg_color: '#333',
	grandchild_page_bg_color: '#989898',
	grandchild_page_color: '#282828',
	grandchild_page_font_weight: 'bold',
	navigation_menu: parseInt( Object.keys( CONFIG.menus )[ 0 ] ?? 0 ),
	parent_page_bg_color: '#666',
	parent_page_color: '#fff',
};

export const block = CONFIG.blocks.navigation;

export const name = block.id;

export const settings: BlockSettings<Attr, '', LegacyWidget<Attr & { title: string }>> = {
	title: __( 'Advanced Sidebar - Navigation', 'advanced-sidebar-menu' ),
	icon: 'welcome-widgets-menus',
	category: 'widgets',
	example: {
		attributes: EXAMPLE,
	},
	transforms: {
		from: [
			{
				type: 'block',
				blocks: [ 'core/legacy-widget' ],
				isMatch: ( {idBase, instance}: LegacyWidget<Attr & { title: string }> ) => {
					if ( null === instance?.raw ) {
						// Can't transform if raw instance is not shown in REST API.
						return false;
					}
					return 'advanced-sidebar-navigation-menu' === idBase;
				},
				transform: window.ADVANCED_SIDEBAR_MENU.transformLegacyWidget( name ),
			},
		],
	},
	attributes: block.attributes,
	supports: block.supports,
	edit: props => (
		<Edit {...props} />
	),
	save: () => null,
	apiVersion: 3,
};
